import ExcelJS from "exceljs";
import { Alert, Button, Card, Space, Typography } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router";
import { Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import { ContactInformationIndexFileName, UploadExcelFileType } from "@/config";
import RequiredMark from "@/views/components/RequiredMark";

export interface ShipContactImportRow {
    shipId: string;
    cnname: string;
    captain: string;
    mobile: string;
    contactMaintainer?: string;
    userId?: string;
    errors: string[];
}

export interface ShipImportBatchResponse {
    errors?: Array<{ index: number; errorMsg: string }>;
}

export interface ShipContactInformationUploadPageProps {
    initialRows?: Array<Partial<ShipContactImportRow>>;
    validateBatch?: (rows: ShipContactImportRow[]) => Promise<ShipImportBatchResponse>;
    submitBatch?: (rows: ShipContactImportRow[]) => Promise<unknown>;
}

/** 校验 Vue 联系方式导入模板的固定表头。 */
export function validateShipContactTemplate(headers: string[]): boolean {
    return ["MMSI", "中文船名", "负责人", "联系方式", "联系方式维护人", "工号"].every(
        (header, index) => headers[index]?.trim() === header
    );
}

/** 将 Excel 行转换为 Vue 联系方式导入行，并保留必填错误。 */
export function buildShipContactImportRows(rows: string[][]): ShipContactImportRow[] {
    return rows
        .filter(values => values.some(value => value.trim() !== ""))
        .map(values => {
            const row: ShipContactImportRow = {
                shipId: values[0]?.trim() || "",
                cnname: values[1]?.trim() || "",
                captain: values[2]?.trim() || "",
                mobile: values[3]?.trim() || "",
                contactMaintainer: values[4]?.trim() || "",
                userId: values[5]?.trim() || "",
                errors: []
            };
            if (!row.shipId) row.errors.push("MMSI为空");
            if (!row.mobile) row.errors.push("联系方式为空");
            return row;
        });
}

/** 解析第一张 Excel 表，沿用 Vue 模板校验和 300 条上限。 */
export async function parseShipContactWorkbook(file: Blob): Promise<ShipContactImportRow[]> {
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(await file.arrayBuffer());
    const worksheet = workbook.worksheets[0];
    const rows: string[][] = [];
    worksheet.eachRow(row => rows.push((row.values as unknown[]).slice(1).map(value => String(value ?? ""))));
    if (!validateShipContactTemplate(rows[0] || [])) throw new Error("当前的Excel与模板不一致，请检查!");
    const importRows = buildShipContactImportRows(rows.slice(1));
    if (!importRows.length) throw new Error("Excel中无数据，请添加后再上传!");
    if (importRows.length > 300) throw new Error("导入失败，上限导入为300条数据!");
    return importRows;
}

function contactPayload(rows: ShipContactImportRow[]) {
    return {
        shipContactInformationImportForms: rows.map(({ errors: _errors, ...row }) => row)
    };
}

/** 调用 Vue 联系方式导入 step=1 预检接口。 */
export async function validateShipContactBatch(rows: ShipContactImportRow[]) {
    const response = await shippingRequestHttp.service.post<ShipImportBatchResponse>(
        "/ship-contact-information/batchImport/1",
        contactPayload(rows)
    );
    return response.data;
}

/** 调用 Vue 联系方式导入 step=2 提交接口。 */
export async function submitShipContactBatch(rows: ShipContactImportRow[]) {
    const response = await shippingRequestHttp.service.post(
        "/ship-contact-information/batchImport/2",
        contactPayload(rows)
    );
    return response.data;
}

function normalizeRows(rows: Array<Partial<ShipContactImportRow>>): ShipContactImportRow[] {
    return rows.map(row => {
        const normalized: ShipContactImportRow = {
            shipId: row.shipId || "",
            cnname: row.cnname || "",
            captain: row.captain || "",
            mobile: row.mobile || "",
            contactMaintainer: row.contactMaintainer || "",
            userId: row.userId || "",
            errors: row.errors ? [...row.errors] : []
        };
        if (!normalized.shipId && !normalized.errors.includes("MMSI为空")) normalized.errors.push("MMSI为空");
        if (!normalized.mobile && !normalized.errors.includes("联系方式为空")) normalized.errors.push("联系方式为空");
        return normalized;
    });
}

/** 船舶联系方式导入页，覆盖 Vue 文件解析、预检和提交状态。 */
export function ShipContactInformationUploadPage({
    initialRows = [],
    validateBatch = validateShipContactBatch,
    submitBatch = submitShipContactBatch
}: ShipContactInformationUploadPageProps) {
    const navigate = useNavigate();
    const [rows, setRows] = useState(() => normalizeRows(initialRows));
    const [feedback, setFeedback] = useState("");

    // 用 zhihao-ui-react 列契约承接 Vue 联系方式导入预览的字段和校验状态。
    const columns: TableColumnProps<ShipContactImportRow>[] = [
        { key: "shipId", label: "MMSI", minWidth: 120, prop: "shipId" },
        { key: "cnname", label: "中文船名", minWidth: 120, prop: "cnname" },
        { key: "captain", label: "负责人", minWidth: 120, prop: "captain" },
        { key: "mobile", label: "联系方式", minWidth: 120, prop: "mobile" },
        { key: "contactMaintainer", label: "联系方式维护人", minWidth: 120, prop: "contactMaintainer" },
        { key: "userId", label: "工号", minWidth: 120, prop: "userId" },
        {
            key: "errors",
            label: "数据校验状态",
            minWidth: 300,
            prop: "errors",
            render: ({ row }) => row.errors.join("，") || "数据校验成功"
        }
    ];

    /** 读取 Excel 并将解析结果展示为可提交的预览数据。 */
    const handleFileChange = async (file?: File) => {
        if (!file) return;
        if (!UploadExcelFileType.includes(file.type)) {
            setFeedback("文件须为Excel格式");
            return;
        }
        try {
            setRows(await parseShipContactWorkbook(file));
            setFeedback("");
        } catch (error) {
            setFeedback(error instanceof Error ? error.message : "Excel解析失败");
        }
    };

    /** 先调用 Vue step=1，服务端错误按行回填到预览表。 */
    const handleValidate = async () => {
        if (!rows.length) {
            setFeedback("请先上传Excel文件");
            return;
        }
        if (rows.some(row => row.errors.length)) {
            setFeedback("数据有误，请修正后再次导入！");
            return;
        }
        try {
            const result = await validateBatch(rows);
            if (result.errors?.length) {
                setRows(current =>
                    current.map((row, index) => ({
                        ...row,
                        errors:
                            result.errors?.filter(error => error.index === index + 1).map(error => error.errorMsg) || []
                    }))
                );
                setFeedback("数据有误，请修正后再次导入！");
            } else setFeedback("数据校验成功");
        } catch {
            setFeedback("导入校验失败");
        }
    };

    /** 仅允许无错误数据进入 Vue step=2 提交。 */
    const handleSubmit = async () => {
        if (!rows.length) {
            setFeedback("请上传正确数据！");
            return;
        }
        if (rows.some(row => row.errors.length)) {
            setFeedback(rows.flatMap(row => row.errors).join("，"));
            return;
        }
        try {
            await submitBatch(rows);
            setFeedback("上传成功！");
            // 沿用 Vue close 行为，提交成功后返回进入导入页之前的页面。
            navigate(-1);
        } catch {
            setFeedback("导入失败");
        }
    };

    return (
        <section aria-label="ship-contact-information-upload-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <Space orientation="vertical" size="middle">
                    <Typography.Title level={4} className="mb-0">
                        联系方式导入
                    </Typography.Title>
                    <Space>
                        <a href={ContactInformationIndexFileName} download="联系方式导入模板.xlsx">
                            下载模版
                        </a>
                        <span>文件仅限 Excel，单次只支持上传300条数据</span>
                    </Space>
                    <label htmlFor="ship-contact-excel">
                        <RequiredMark />
                        选择Excel文件
                    </label>
                    <input
                        id="ship-contact-excel"
                        aria-label="联系方式Excel文件"
                        type="file"
                        accept={UploadExcelFileType.join(",")}
                        onChange={event => void handleFileChange(event.target.files?.[0])}
                    />
                </Space>
            </Card>
            {feedback ? <Alert title={feedback} role="alert" type="info" /> : null}
            <Card>
                <ZhTable<ShipContactImportRow>
                    data={rows}
                    columns={columns}
                    pagination={false}
                    rowKey={row => row.shipId || String(row.cnname)}
                />
            </Card>
            <Space>
                <Button type="primary" onClick={() => void handleValidate()}>
                    校验导入数据
                </Button>
                <Button type="primary" onClick={() => void handleSubmit()}>
                    保存导入
                </Button>
                <Button onClick={() => navigate(-1)}>返回</Button>
            </Space>
        </section>
    );
}

export default ShipContactInformationUploadPage;
